import React, { useState, useEffect } from "react";
import {
    Typography,
    Box,
    Paper,
    FormControl,
    FormLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
    MenuItem,
    Select,
    Button,
    InputLabel,
    Modal,
    TextField,
    IconButton,
    LinearProgress,
    Grid,
} from "@mui/material";
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from "@mui/icons-material/Edit";
import { useNavigate, useSearchParams } from "react-router-dom";
import { collection, doc, getDoc, query, where, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import PaymentButton from './PaymentButton';

function Pay() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const vNum = searchParams.get("vNum") || "UNKNOWN";
    const myref = searchParams.get("ref") || "";
    const [paymentType, setPaymentType] = useState("");
    const [location, setLocation] = useState("");
    const [price, setPrice] = useState(null);
    const [hours, setHours] = useState(1);
    const [openModal, setOpenModal] = useState(false);
    const [newVNum, setNewVNum] = useState(vNum);
    const [loading, setLoading] = useState(true);
    const [zoneData, setZoneData] = useState({
        zone1: { capacity: 0, occupied: 0 },
        zone2: { capacity: 0, occupied: 0 },
        zone3: { capacity: 0, occupied: 0 },
        zone4: { capacity: 0, occupied: 0 },
    });

    // Redirect to home if vNum is UNKNOWN
    if (vNum === "UNKNOWN") {
        navigate("/");
    }

    // Check if vNum exists in vehiclemaster collection
    useEffect(() => {
        const checkVehicleExists = async () => {
            try {
                const docRef = doc(db, "vehiclemaster", String(vNum));
                const docSnap = await getDoc(docRef);

                if (docSnap.exists()) {
                    // If vehicle exists and myref is not set (not redirected from /receipt), redirect to receipt
                    if (!myref) {
                        navigate(`/receipt?vNum=${vNum}${myref ? `&ref=${myref}` : ""}`);
                    } else {
                        console.log("Redirected from /receipt, allowing access to Pay page");
                    }
                } else {
                    console.log("First Time Payment");
                }
            } catch (error) {
                console.error("Error checking vehicle existence:", error);
            }
        };

        checkVehicleExists();
    }, [vNum, myref, navigate]);

    const today = new Date().toISOString().split("T")[0];

    // Fetch capacities and active vehicles for each zone
    useEffect(() => {
        const fetchZoneData = async () => {
            try {
                setLoading(true);
                const zones = ["zone1", "zone2", "zone3", "zone4"];
                const currentTime = new Date();

                const newZoneData = { ...zoneData };

                for (const zone of zones) {
                    const docRef = doc(db, "kpgparking", zone);
                    const docSnap = await getDoc(docRef);
                    const capacity = docSnap.exists() ? docSnap.data().capacity || 0 : 0;

                    const q = query(
                        collection(db, "vehiclemaster"),
                        where("location", "==", zone),
                        where("end", ">", currentTime)
                    );
                    const querySnapshot = await getDocs(q);
                    const occupied = querySnapshot.size;

                    newZoneData[zone] = { capacity, occupied };
                }

                setZoneData(newZoneData);
            } catch (error) {
                console.error("Error fetching zone data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchZoneData();
    }, []); // Empty dependency array to run once on mount

    const handleFetchPrice = async () => {
        if (!paymentType || !location) return;

        try {
            const docRef = doc(db, "kpgparking", location);
            const docSnap = await getDoc(docRef);

            if (docSnap.exists()) {
                const data = docSnap.data();

                if (paymentType === "hourly") {
                    setPrice(data.hourly * hours);
                } else if (paymentType === "daily") {
                    setPrice(data.daily);
                } else if (paymentType === "monthly") {
                    setPrice(data.monthly);
                }
            } else {
                alert("Zone data not found.");
            }
        } catch (error) {
            console.error("Firestore fetch error:", error);
        }
    };

    useEffect(() => {
        if (paymentType && location) {
            handleFetchPrice();
        }
    }, [paymentType, location, hours]);

    const formatDate = (date) => {
        const d = new Date(date);
        const day = String(d.getDate()).padStart(2, "0");
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const year = d.getFullYear();
        return `${day}/${month}/${year}`;
    };

    const handlePaymentTypeChange = (e) => {
        const type = e.target.value;
        setPaymentType(type);

        if (type === "hourly") {
            setLocation("");
        } else {
            setLocation("allzones");
        }
    };

    const handleEditVNum = () => {
        setOpenModal(true);
    };

    const handleModalClose = () => {
        setOpenModal(false);
        setNewVNum(vNum);
    };

    const handleUpdateVNum = () => {
        if (newVNum.trim() === "") {
            alert("Please enter a valid vehicle number.");
            return;
        }
        navigate(`/pay?vNum=${newVNum.trim().toUpperCase()}${myref ? `&ref=${myref}` : ""}`);
        setOpenModal(false);
    };

    return (
        <Box
            sx={{
                backgroundColor: "#e6f7ff",
                minHeight: "100vh",
                p: 2,
                fontFamily: "sans-serif",
            }}
        >
            <div className="header">
                <img src="/logo.png" alt="Logo" className="logo" />
                <div className="heading-text">
                    <br />
                    <h1 className="title">Smart Parking App</h1>
                    <p className="caption">"Empowering Digital India .."</p>
                </div>
            </div>
            <br />
            <Accordion
                sx={{
                    borderRadius: "12px", // Rounded borders
                    overflow: "hidden", // Ensures content respects the rounded borders
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)", // Subtle shadow for depth
                    backgroundColor: "#fff", // Clean white background
                    margin: "20px auto", // Margin for spacing from other elements
                    maxWidth: 600, // Match Paper width for consistency
                }}
            >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel1-content"
                    id="panel1-header"
                    sx={{
                        padding: "12px 20px", // Consistent padding
                        "& .MuiAccordionSummary-content": {
                            margin: "10px 0", // Internal spacing for content
                        },
                    }}
                >
                    <div style={{ display: "flex", flexDirection:'column'}}>
                    <Typography variant="h6" sx={{ fontWeight: "medium" }}>
                        Parking Zone Occupancy
                    </Typography>
                    <Typography style={{ fontSize: 12}}>Click on the zones to get details</Typography>
                    </div>
                </AccordionSummary>
                <Box sx={{ padding: "0px 20px 20px" }}> {/* Proper spacing inside Accordion */}
                    
                    {loading && (
                        <Box
                            sx={{
                                position: "absolute",
                                top: "50%",
                                left: "50%",
                                transform: "translate(-50%, -50%)",
                                backgroundColor: "rgba(255, 255, 255, 0.95)",
                                p: 3,
                                borderRadius: 3,
                                boxShadow: 5,
                                zIndex: 10,
                                minWidth: 200,
                                textAlign: "center",
                            }}
                        >
                            <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                                Fetching parking data...
                            </Typography>
                        </Box>
                    )}

                    <Grid container spacing={2} direction="column"> {/* Single-column layout */}
                        {["zone1", "zone2", "zone3", "zone4"].map((zone, index) => {
                            const { capacity, occupied } = zoneData[zone];
                            const percentage = capacity > 0 ? (occupied / capacity) * 100 : 0;
                            const zoneName = {
                                zone1: "Damber Chowk Zone",
                                zone2: "10th Mile Zone",
                                zone3: "9th Mile Zone",
                                zone4: "CK Chowk Zone",
                            }[zone];

                            return (
                                <Grid item xs={12} key={zone}> {/* Full width for each item */}
                                    <Typography
                                        variant="body2"
                                        sx={{ fontWeight: "medium", mb: 1 }} // Margin below text
                                    >
                                        {zoneName}: {occupied}/{capacity} - <a>0.8 Km</a>
                                    </Typography>
                                    <LinearProgress
                                        variant="determinate"
                                        value={percentage}
                                        sx={{
                                            height: 10,
                                            borderRadius: 5,
                                            backgroundColor: "#e0e0e0",
                                            "& .MuiLinearProgress-bar": {
                                                backgroundColor: percentage > 80 ? "#d32f2f" : "#5b9bd5",
                                            },
                                        }}
                                    />
                                </Grid>
                            );
                        })}
                    </Grid>
                </Box>
            </Accordion>
            <Paper
                elevation={3}
                sx={{
                    maxWidth: 600,
                    margin: "auto",
                    p: 4,
                    borderRadius: 3,
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <Typography variant="h6">
                        Parking Details for <strong>{vNum}</strong>
                    </Typography>
                    <IconButton onClick={handleEditVNum} sx={{ ml: 1 }}>
                        <EditIcon sx={{ color: "#5b9bd5" }} />
                    </IconButton>
                </Box>

                <FormControl fullWidth sx={{ mb: 3 }}>
                    <FormLabel>Payment Type</FormLabel>
                    <RadioGroup
                        row
                        value={paymentType}
                        onChange={handlePaymentTypeChange}
                    >
                        <FormControlLabel value="hourly" control={<Radio />} label="Hourly" />
                        <FormControlLabel value="daily" control={<Radio />} label="For Today" />
                        <FormControlLabel value="monthly" control={<Radio />} label="Monthly" />
                    </RadioGroup>
                </FormControl>

                {paymentType === "hourly" && (
                    <FormControl fullWidth sx={{ mb: 3 }}>
                        <FormLabel>Select Hours</FormLabel>
                        <Select
                            value={hours}
                            onChange={(e) => setHours(Number(e.target.value))}
                        >
                            {[...Array(12)].map((_, i) => (
                                <MenuItem key={i + 1} value={i + 1}>
                                    {i + 1} hour(s)
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}

                {paymentType === "daily" && (
                    <Typography variant="body1" mb={3}>
                        Date: <strong>{formatDate(new Date())}</strong>
                    </Typography>
                )}

                {paymentType === "monthly" && (
                    <Typography variant="body1" mb={3}>
                        Validity: <strong>{formatDate(new Date())}</strong> to{" "}
                        <strong>{formatDate(new Date(new Date().setMonth(new Date().getMonth() + 1)))}</strong>
                    </Typography>
                )}

                {paymentType === "hourly" && (
                    <FormControl fullWidth sx={{ mb: 3 }}>
                        <InputLabel>Parking Location</InputLabel>
                        <Select
                            value={location}
                            label="Parking Location"
                            onChange={(e) => setLocation(e.target.value)}
                        >
                            <MenuItem value="zone1">Damber Chowk Zone</MenuItem>
                            <MenuItem value="zone2">10th Mile Zone</MenuItem>
                            <MenuItem value="zone3">9th Mile Zone</MenuItem>
                            <MenuItem value="zone4">CK Chowk Zone</MenuItem>
                        </Select>
                    </FormControl>
                )}

                {price !== null && (
                    <Typography variant="h6" sx={{ mb: 3 }}>
                        Total Price: ₹ <strong>{price}</strong>
                    </Typography>
                )}

                <PaymentButton
                    amount={price || 0}
                    vNum={vNum}
                    paymentType={paymentType}
                    hours={hours}
                    location={location}
                />
            </Paper>

            <Modal open={openModal} onClose={handleModalClose}>
                <Box
                    sx={{
                        backgroundColor: "#fff",
                        boxShadow: 24,
                        p: 4,
                        borderRadius: 2,
                        width: "80%",
                        maxWidth: 400,
                        textAlign: "center",
                        margin: "auto",
                        mt: "20vh",
                    }}
                >
                    <Typography variant="h6" mb={3}>
                        Edit Vehicle Number
                    </Typography>
                    <TextField
                        label="Vehicle Number"
                        variant="outlined"
                        fullWidth
                        value={newVNum}
                        onChange={(e) => setNewVNum(e.target.value)}
                        sx={{ mb: 3 }}
                    />
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Button
                            variant="outlined"
                            onClick={handleModalClose}
                            sx={{
                                width: "45%",
                                color: "#5b9bd5",
                                borderColor: "#5b9bd5",
                                "&:hover": { borderColor: "#4a8ac2", backgroundColor: "#f0f8ff" },
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleUpdateVNum}
                            sx={{
                                width: "45%",
                                backgroundColor: "#5b9bd5",
                                "&:hover": { backgroundColor: "#4a8ac2" },
                            }}
                        >
                            Update
                        </Button>
                    </Box>
                </Box>
            </Modal>
        </Box>
    );
}

export default Pay;